import { Link } from 'react-router-dom';
import { CheckIcon } from './icons';
import type { OnboardingStatus } from './types';

/**
 * Step 3 — setup complete. Confirms the install state + links to the dashboard
 * and HubSpot (where the sidebar/admin cards live).
 *
 * QA-F #5: when the wizard lands here because the import already completed,
 * `importStats` carries the final processed/failed counts and we print them so
 * the user sees what was imported (not just "under way").
 */
export function StepDone({
  status,
  importStats,
}: {
  status: OnboardingStatus | null;
  importStats: { totalProcessed: number; totalFailed: number; } | null;
}) {
  return (
    <div className="card mx-auto max-w-xl p-8 text-center">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-success-500/10 text-success-600">
        <CheckIcon className="h-7 w-7" />
      </div>
      <h1 className="text-xl font-semibold text-ink-900">You're all set</h1>
      <p className="mt-2 text-sm text-ink-500">
        Stripe Sync is now running. New Stripe events will mirror into HubSpot
        automatically.
      </p>

      {/* Import completion stats (QA-F #5). Only shown when the import has
          finished — not when this step is reached straight from the Import
          step's "Continue without waiting" (importStats stays null then, since
          the import is still running). */}
      {importStats && (
        <div className="mt-5 flex items-center justify-center gap-6 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-400">
              Imported
            </div>
            <div className="text-lg font-semibold text-ink-900">
              {importStats.totalProcessed.toLocaleString()}
            </div>
          </div>
          {importStats.totalFailed > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-400">
                Failed
              </div>
              <div className="text-lg font-semibold text-warn-600">
                {importStats.totalFailed.toLocaleString()}
              </div>
            </div>
          )}
        </div>
      )}

      {status?.stripeAccountId && (
        <p className="mt-4 inline-block rounded-md bg-ink-50 px-3 py-1.5 font-mono text-xs text-ink-600">
          {status.stripeAccountId}
        </p>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/dashboard" className="btn-primary">
          Go to dashboard
        </Link>
        <a
          href="https://app.hubspot.com"
          target="_blank"
          rel="noreferrer"
          className="btn-secondary"
        >
          Open HubSpot
        </a>
      </div>

      <p className="mt-8 text-xs text-ink-400">
        Tip: open any Contact in HubSpot to see their Stripe subscription,
        lifetime value, and recent invoices in the sidebar card.
      </p>
    </div>
  );
}
